import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { 
  Users, Video, Shield, TrendingUp, CheckCircle, 
  ArrowRight, DollarSign, Eye, Lock, Zap, Award
} from 'lucide-react';

const TalentLanding = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-green-600 via-teal-600 to-green-700 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>

        <div className="container mx-auto px-4 py-20 md:py-32 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight text-white drop-shadow-lg">
              Let Employers Find You
            </h1>
            <p className="text-xl md:text-2xl mb-12 text-green-100 max-w-3xl mx-auto drop-shadow">
              Create your professional profile. Get discovered by top Australian employers. Receive job offers directly.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <button
                onClick={() => user ? navigate('/talent/me') : navigate('/register')}
                className="px-8 py-4 bg-white text-green-600 font-semibold rounded-xl hover:bg-green-50 transition-all transform hover:scale-105 shadow-xl flex items-center justify-center gap-2 text-lg"
              >
                <Users className="w-6 h-6" />
                Activate GoTalent
              </button>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-6 text-white">
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm">
                <Users className="w-5 h-5" />
                <span className="font-medium">100+ Active Profiles</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm">
                <Award className="w-5 h-5" />
                <span className="font-medium">Top Employers</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm">
                <Shield className="w-5 h-5" />
                <span className="font-medium">Privacy Protected</span>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" className="fill-background"/>
          </svg>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">How GoTalent Works</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Get discovered by employers in three simple steps
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center p-6 rounded-2xl bg-card hover:bg-muted/50 transition-colors border border-border">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 text-white rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg">
                1
              </div>
              <Video className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-3 text-foreground">Create Your Profile</h3>
              <p className="text-muted-foreground">
                Build your professional profile and record a 60-second video introduction
              </p>
            </div>

            <div className="text-center p-6 rounded-2xl bg-card hover:bg-muted/50 transition-colors border border-border">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 text-white rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg">
                2
              </div>
              <Eye className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-3 text-foreground">Become Discoverable</h3>
              <p className="text-muted-foreground">
                Activate your subscription and toggle on discoverability to be found by employers
              </p>
            </div>

            <div className="text-center p-6 rounded-2xl bg-card hover:bg-muted/50 transition-colors border border-border">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 text-white rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg">
                3
              </div>
              <Zap className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-3 text-foreground">Receive Offers</h3>
              <p className="text-muted-foreground">
                Get job offers and interview invites directly from interested employers
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-12 text-foreground">Why GoTalent?</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-card p-6 rounded-xl border border-border">
                <Shield className="w-10 h-10 text-green-600 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-foreground">Privacy First</h3>
                <p className="text-muted-foreground">
                  Your contact details stay hidden. Employers message you in-app. You control who sees what.
                </p>
              </div>

              <div className="bg-card p-6 rounded-xl border border-border">
                <DollarSign className="w-10 h-10 text-green-600 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-foreground">Affordable Access</h3>
                <p className="text-muted-foreground">
                  Just $3 for 3 months. No hidden fees. Cancel anytime. Full control of your visibility.
                </p>
              </div>

              <div className="bg-card p-6 rounded-xl border border-border">
                <Video className="w-10 h-10 text-green-600 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-foreground">Stand Out with Video</h3>
                <p className="text-muted-foreground">
                  Your 60-second video intro helps employers see your personality and passion.
                </p>
              </div>

              <div className="bg-card p-6 rounded-xl border border-border">
                <TrendingUp className="w-10 h-10 text-green-600 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-foreground">Passive Job Search</h3>
                <p className="text-muted-foreground">
                  Let opportunities come to you while you focus on your current role or studies.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-foreground">Simple, Affordable Pricing</h2>
            <p className="text-xl text-muted-foreground">
              Choose the plan that works for you
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="bg-card border-2 border-border rounded-2xl p-6 hover:border-green-300 dark:hover:border-green-600 transition-colors">
              <div className="text-center">
                <h3 className="text-xl font-bold mb-2 text-foreground">3 Months</h3>
                <div className="text-4xl font-bold text-green-600 mb-2">$3</div>
                <p className="text-sm text-muted-foreground mb-6">$1 per month</p>
                <ul className="space-y-2 text-sm mb-6 text-left">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                    <span>Profile discoverable</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                    <span>Video intro</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                    <span>Receive offers</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-teal-50 dark:from-green-950 dark:to-teal-950 border-2 border-green-500 rounded-2xl p-6 relative">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-green-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                Best Value
              </div>
              <div className="text-center">
                <h3 className="text-xl font-bold mb-2 text-foreground">6 Months</h3>
                <div className="text-4xl font-bold text-green-600 mb-2">$6</div>
                <p className="text-sm text-muted-foreground mb-6">$1 per month</p>
                <ul className="space-y-2 text-sm mb-6 text-left">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                    <span>Everything in 3 months</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                    <span>Extended visibility</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                    <span>Best value</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-card border-2 border-border rounded-2xl p-6 hover:border-green-300 dark:hover:border-green-600 transition-colors">
              <div className="text-center">
                <h3 className="text-xl font-bold mb-2 text-foreground">12 Months</h3>
                <div className="text-4xl font-bold text-green-600 mb-2">$12</div>
                <p className="text-sm text-muted-foreground mb-6">$1 per month</p>
                <ul className="space-y-2 text-sm mb-6 text-left">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                    <span>Everything in 6 months</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                    <span>Full year coverage</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                    <span>Maximum exposure</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="text-center mt-8">
            <button
              onClick={() => user ? navigate('/talent/me') : navigate('/register')}
              className="inline-flex items-center gap-2 px-8 py-4 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 transition-all shadow-lg"
            >
              Get Started
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-12 text-foreground">Frequently Asked Questions</h2>
            
            <div className="space-y-4">
              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="font-semibold text-lg mb-2 text-foreground">Who can see my profile?</h3>
                <p className="text-muted-foreground">
                  Only verified employers with an active GoTalent access pass can view your full profile and contact details. 
                  You control your visibility with a simple on/off toggle.
                </p>
              </div>

              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="font-semibold text-lg mb-2 text-foreground">Can I turn it off anytime?</h3>
                <p className="text-muted-foreground">
                  Yes! You can toggle your discoverability on or off instantly. When off, your profile is hidden from employer searches.
                  You can also cancel your subscription anytime.
                </p>
              </div>

              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="font-semibold text-lg mb-2 text-foreground">What information is visible to employers?</h3>
                <p className="text-muted-foreground">
                  Employers see your professional profile: skills, experience, desired roles, video intro, and resume (if uploaded).
                  Your email and phone remain hidden unless you choose to share via messaging. All communication happens securely in GoFella.
                </p>
              </div>

              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="font-semibold text-lg mb-2 text-foreground">Is my information secure?</h3>
                <p className="text-muted-foreground">
                  Yes. Your contact details are never shown publicly. All media is stored securely and accessed via encrypted connections.
                  You can block companies and report any inappropriate behavior.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-green-600 via-teal-600 to-green-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white drop-shadow-lg">
              Ready to Get Discovered?
            </h2>
            <p className="text-xl text-green-100 mb-8 drop-shadow">
              Join GoTalent today and let top employers find you
            </p>
            
            <button
              onClick={() => user ? navigate('/talent/me') : navigate('/register')}
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-green-600 font-semibold rounded-xl hover:bg-green-50 transition-all shadow-xl text-lg"
            >
              Start Now - From $3
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TalentLanding;
